import { useState } from 'react';
import { Modal } from '@mantine/core';
import { canReviewSendingList, type NewsletterProtectionDecision } from './newsletterProtection';
import type { CampaignAddress } from './newsletterUtils';

function suspensionReason(decision: NewsletterProtectionDecision) {
  const incidents = decision.incidents.filter(incident =>
    incident.action === 'pause' && decision.action.reason_codes.includes(incident.code));
  const incident = incidents.find(item => item.scope === 'campaign') || incidents[0];
  if (!incident) return 'Des incidents ont été détectés. Vérifiez votre liste avant de reprendre.';
  const description = incident.code === 'complaints'
    ? `${incident.count} signalement${incident.count === 1 ? '' : 's'} de spam`
    : `${incident.count} adresse${incident.count === 1 ? '' : 's'} rejetée${incident.count === 1 ? '' : 's'}`;
  return `${description} sur ${incident.denominator} envois${incident.scope === 'campaign' ? ' de cette campagne' : ' récents'}.`;
}

export function NewsletterRecoveryNotice({ decision, addresses, unavailable, onRequestRecovery }: {
  decision: NewsletterProtectionDecision;
  addresses: CampaignAddress[];
  unavailable?: boolean;
  onRequestRecovery: () => Promise<unknown>;
}) {
  const [opened, setOpened] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canRecover = canReviewSendingList(decision);
  const reason = suspensionReason(decision);
  const close = () => {
    if (busy) return;
    setOpened(false);
    setReviewed(false);
    setError('');
  };

  return <>
    <section className="newsletter-recovery-notice" aria-label="Envoi suspendu">
      <strong>{canRecover ? 'Action requise : vérifiez votre liste' : 'Vérification en cours par Relyo'}</strong>
      <p>{canRecover ? reason : 'Cette suspension nécessite une vérification par Relyo. Vous ne pouvez pas reprendre l’envoi vous-même.'}</p>
      {canRecover && <button type="button" className="button" disabled={unavailable || busy}
        onClick={() => {setReviewed(false); setError(''); setOpened(true);}}>Vérifier et reprendre</button>}
    </section>
    <Modal opened={opened && canRecover} onClose={close} title="Vérifier et reprendre" centered size="md"
      closeButtonProps={{'aria-label': 'Fermer'}} classNames={{content:'newsletter-recovery-modal'}}>
      <div className="newsletter-recovery-dialog">
        <p>{reason}</p>
        {addresses.length > 0 && <div>
          <strong>Adresses signalées dans cette campagne</strong>
          <ul className="newsletter-recovery-addresses">{addresses.map(address => <li key={address.email}>{address.email}</li>)}</ul>
        </div>}
        {decision.action.reason_codes.includes('complaints') && <p>Les adresses liées aux signalements de spam ne sont pas disponibles dans ce rapport.</p>}
        <p>Vérifiez les adresses signalées et assurez-vous que le reste de votre liste contient des personnes qui attendent vos messages.</p>
        <p>Seuls les emails restants reprendront, progressivement et selon la capacité disponible. Les emails déjà envoyés ne seront pas renvoyés.</p>
        <label className="newsletter-recovery-review">
          <input type="checkbox" checked={reviewed} disabled={busy} onChange={event => setReviewed(event.target.checked)} />
          J’ai vérifié ma liste.
        </label>
        {unavailable && <p role="alert">La protection doit être actualisée avant de reprendre. Réessayez dans un instant.</p>}
        {error && <p role="alert">{error}</p>}
        <div className="newsletter-recovery-actions">
          <button type="button" className="button button-secondary" disabled={busy} onClick={close}>Annuler</button>
          <button type="button" className="button" disabled={!reviewed || busy || unavailable} onClick={async () => {
            if (!reviewed || busy || unavailable) return;
            setBusy(true); setError('');
            try {await onRequestRecovery(); setOpened(false); setReviewed(false);}
            catch {setError('La reprise n’a pas pu être autorisée. Réessayez ou contactez Relyo.'); setReviewed(false);}
            finally {setBusy(false);}
          }}>{busy ? 'Reprise en cours…' : 'Reprendre l’envoi'}</button>
        </div>
      </div>
    </Modal>
  </>;
}
